/**
 * CORS support configuration for CEX exchanges.
 *
 * Based on documentation research (Feb 2026).
 * This configuration determines which exchanges can fallback to direct
 * browser requests when proxy requests fail/timeout.
 *
 * Support levels:
 * - FULL: All public endpoints work from browser (Access-Control-Allow-Origin: *)
 * - PARTIAL: Only some endpoints work (restrictions apply)
 * - NONE: Proxy required for all requests (CORS blocked)
 *
 * @see TR-1192 for research details
 */

import { ECexName } from 'web3';

export type CorsSupport = 'FULL' | 'PARTIAL' | 'NONE';

/**
 * CORS support map for all supported exchanges.
 *
 * Research sources:
 * - Official API documentation
 * - Direct browser testing with Origin headers
 * - CCXT library configurations
 */
export const EXCHANGE_CORS_SUPPORT: Record<ECexName, CorsSupport> = {
  // Full CORS support - all public endpoints work from browser
  [ECexName.binance]: 'FULL', // Access-Control-Allow-Origin: *
  [ECexName.okx]: 'FULL', // Dynamic origin reflection
  [ECexName.bybit]: 'FULL', // Works, but geo-blocking for US/China IPs
  [ECexName.bitget]: 'FULL', // Public endpoints only
  [ECexName.kraken]: 'FULL', // Dynamic origin reflection
  [ECexName.coinbase]: 'FULL', // Verified live with Origin header on 2026-07-20
  [ECexName.cryptocom]: 'FULL', // GET and OPTIONS reflect dashboard origin; verified 2026-07-20
  [ECexName.bitvavo]: 'FULL', // Public REST returned ACAO * with dashboard Origin; verified 2026-07-20
  [ECexName.bithumb]: 'FULL', // Simple public GETs return ACAO *; verified 2026-07-20
  [ECexName.coinstore]: 'FULL', // Public endpoints only
  [ECexName.hotcoin]: 'FULL', // Works without issues

  // Partial CORS - some restrictions apply
  [ECexName.htx]: 'PARTIAL', // Only simple requests (no custom headers like Content-Type)
  [ECexName.indodax]: 'NONE', // Only /tradingview/* works, main API blocked - treat as NONE

  // No CORS support - proxy required for all requests
  [ECexName.gate]: 'NONE', // Explicitly blocked
  [ECexName.kucoin]: 'NONE', // GitHub issue open since 2022
  [ECexName.mexc]: 'NONE', // 403 on preflight
  [ECexName.bitmart]: 'NONE', // No CORS headers
  [ECexName.bingx]: 'NONE', // Whitelist only for *.bingx.com
  [ECexName.xt]: 'NONE', // 403 on OPTIONS
  [ECexName.lbank]: 'NONE', // All endpoints blocked
  [ECexName.whitebit]: 'NONE', // Explicitly stated in FAQ as forbidden
  [ECexName.weex]: 'NONE', // New exchange; proxy-only until CORS is verified
  [ECexName.biconomy]: 'NONE', // New exchange; proxy-only until CORS is verified
  [ECexName.coinw]: 'NONE', // Token-gated socket.io; proxy-only
  [ECexName.upbit]: 'NONE', // Origin rate group throttles parallel public requests; proxy-only
  [ECexName.bitstamp]: 'NONE', // Public endpoint CORS is inconsistent; proxy-only
  [ECexName.toobit]: 'NONE', // No ACAO on GET and OPTIONS returned 403; verified 2026-07-20
  [ECexName.hashkey]: 'NONE', // Chromium XHR omitted ACAO for all four public REST calls; verified 2026-07-20
  [ECexName.bullish]: 'NONE', // Origin GET/OPTIONS returned no ACAO; verified 2026-07-20
  [ECexName.gemini]: 'NONE', // Book/trades omit ACAO; use one proxy path for all REST, verified 2026-07-20
  [ECexName.bitfinex]: 'NONE', // Public REST omits ACAO; proxy-only, verified 2026-07-21
};

/**
 * Check if an exchange supports direct browser requests as fallback.
 *
 * Returns true for exchanges with FULL or PARTIAL CORS support.
 * For PARTIAL support, the fallback may still fail for some endpoints,
 * but it's worth attempting.
 *
 * @param exchange - Exchange identifier
 * @returns true if direct browser requests can be attempted as fallback
 */
export const canFallbackDirect = (exchange: ECexName): boolean => {
  const support = EXCHANGE_CORS_SUPPORT[exchange];
  return support === 'FULL' || support === 'PARTIAL';
};

/**
 * Get CORS support level for an exchange.
 *
 * @param exchange - Exchange identifier
 * @returns CORS support level
 */
export const getCorsSupport = (exchange: ECexName): CorsSupport => {
  return EXCHANGE_CORS_SUPPORT[exchange] ?? 'NONE';
};
